import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Badge — uses tonal (alpha-mixed) backgrounds rather than solid pills,
 * so several can sit next to each other without screaming.
 */
const badge = cva(
  [
    "inline-flex items-center gap-1 px-2 py-0.5 rounded-[var(--radius-full)]",
    "text-[11px] font-medium tracking-[-0.005em] tabular-nums",
    "border border-transparent",
  ],
  {
    variants: {
      tone: {
        neutral:
          "bg-[var(--bg-subtle)] text-[var(--fg-muted)]",
        info:
          "bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-[var(--accent)]",
        success:
          "bg-[color-mix(in_oklab,var(--success)_18%,transparent)] text-[color-mix(in_oklab,var(--success)_60%,#0d3d24)]",
        caution:
          "bg-[color-mix(in_oklab,var(--caution)_22%,transparent)] text-[color-mix(in_oklab,var(--caution)_55%,#3d2d05)]",
        warning:
          "bg-[color-mix(in_oklab,var(--warning)_18%,transparent)] text-[color-mix(in_oklab,var(--warning)_60%,#3d1f05)]",
        danger:
          "bg-[color-mix(in_oklab,var(--danger)_18%,transparent)] text-[color-mix(in_oklab,var(--danger)_60%,#3d0a0a)]",
      },
      solid: {
        true: "border-transparent text-white",
        false: "",
      },
    },
    compoundVariants: [
      { solid: true, tone: "info",    class: "bg-[var(--accent-solid)] text-[var(--accent-fg)]" },
      { solid: true, tone: "success", class: "bg-[var(--success)] text-black/85" },
      { solid: true, tone: "caution", class: "bg-[var(--caution)] text-black/85" },
      { solid: true, tone: "warning", class: "bg-[var(--warning)] text-white" },
      { solid: true, tone: "danger",  class: "bg-[var(--danger)] text-white" },
    ],
    defaultVariants: { tone: "neutral", solid: false },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badge> {}

export function Badge({ className, tone, solid, ...p }: BadgeProps) {
  return <span className={cn(badge({ tone, solid }), className)} {...p} />;
}
